<template>
	<div>
		<!-- show sorting options -->
		<div class="row justify-between q-col-gutter-sm">
			<div class="col">
				<search-type-select @updatedSearchType="setSearchType" v-if="!isPublic"/>
			</div>
			<div class="col col-shrink">
				<div class="column">
					<div class="col">
						<span class="text-overline">{{ $t('sorting') }}</span>
					</div>
					<div class="col">
						<sorting-select @updatedSortingDirection="changeSortingDirection"
										@updatedSorting="setSorting"
										:options="sortingOptions"
										:disable="switchingSearchTypes"
						/>
					</div>
				</div>
			</div>
			<div class="col col-shrink">
				<search-view-select @input="setSearchDisplayType"/>
			</div>
		</div>

		<!-- show results -->
		<template v-if="displayType === 'teaser'">
			<q-infinite-scroll @load="loadAdditionalResults"
							   :offset="6000"
							   :debounce="100"
							   :disable="disableInfiniteScroll"
			>
				<div class="row q-py-lg q-col-gutter-md">
					<div v-for="object in logSearchResults"
						 :key="object.uid"
						 class="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
					>
						<!-- displaying veneer logs -->
						<veneer-log v-if="isLogSearch"
									:log="object"
									:watchlist-dialog="showWatchlistDialog"
									:measurement-list-dialog="true"
									:show-active-watchlist-check="true"
									:is-dynamic-watchlist="isDynamicWatchlist"
									:is-public="isPublic"
						/>

						<!-- displaying pallets -->
						<pallet v-if="isPalletSearch"
								:pallet="object"
						/>
						<button v-if="showAddToCurrentWatchlistButton" @click="addObjectToWatchlist(object, watchlist)">{{ $t('button.addToWatchlist') }}</button>
					</div>
				</div>

				<template v-slot:loading>
					<div class="row justify-center q-my-md">
						<q-spinner-dots color="primary" size="3em"/>
					</div>
				</template>
			</q-infinite-scroll>

			<q-spinner-cube v-if="displayInitialSearchSpinner" color="primary" size="5em" class="absolute-center"/>
		</template>

		<template v-if="displayType === 'table'">
			<div class="row q-py-lg justify-center">
				<veneer-log-table v-if="isLogSearch" @load-additional-results="loadAdditionalResults"/>
				<pallet-table v-if="isPalletSearch"/>
			</div>
		</template>

		<!-- empty result state -->
		<div v-if="!logSearchResults.length && !loadingLogs && displayType === 'teaser'" class="text-center">
			<q-icon name="clear" size="5em" color="primary"/>
			<span v-if="sufficientSearchParamsGiven" class="text-h5 vertical-middle">{{ $t('noResults') }}</span>
			<span v-else class="text-h5 vertical-middle">{{ $t('noSearchParams') }}</span>
		</div>
	</div>
</template>

<script>
	import VeneerLog from "@/components/common/VeneerLog";
  import {
    SET_DETAIL_VIEW_BACK_LINK,
    SET_LOADING_LOGS, SET_SEARCH_DISPLAY_TYPE, SET_SEARCH_DISPLAY_TYPE_PALLET,
    SET_SEARCH_PARAMS,
    SET_SEARCH_TYPE
  } from "@/store/mutations.type";
	import {mapGetters} from "vuex";
	import LogSearchMixin from "@/mixins/LogSearchMixin";
	import LogUtility from "@/mixins/LogUtility";
	import VeneerLogTable from "@/components/common/VeneerLogTable";
	import woodtypes from "@/common/woodtypes";
	import Pallet from "@/components/common/Pallet";
	import SearchViewSelect from "@/components/form/SearchViewSelect";
	import PalletTable from "@/components/common/pallet/PalletTable";
	import {SEARCH_TYPE} from "@/common/constants";
	import SearchTypeSelect from "@/components/form/SearchTypeSelect";
	import SortingSelect from "@/components/form/SortingSelect";

	export default {
		name: "LogSearch",
		mixins: [LogSearchMixin, LogUtility],
		components: {SortingSelect, SearchTypeSelect, PalletTable, SearchViewSelect, Pallet, VeneerLogTable, VeneerLog},
		props: {
			showWatchlistDialog: {
				type: Boolean,
				default: true
			},
			showAddToCurrentWatchlistButton: {
				type: Boolean,
				default: false
			},
			isDynamicWatchlist: {
				type: Boolean,
				default: false
			},
			isPublic: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				switchingSearchTypes: false
			}
		},
    watch: {
      watchlist(newValue, oldValue) {
        if(oldValue && newValue.showBundleImages !== oldValue.showBundleImages) {
          this.loadResults();
        }
      }
    },
		computed: {
			...mapGetters(['watchlist', 'searchType', 'searchDisplayType', 'searchDisplayTypePallet', 'logSearchRequest']),
			isLogSearch() {
				return this.searchType === SEARCH_TYPE.BLOCK;
			},
			isPalletSearch() {
				return this.searchType === SEARCH_TYPE.PALLET;
			},
      displayType() {
        if(this.isLogSearch) {
          return this.searchDisplayType;
        }
        return this.searchDisplayTypePallet;
      },
			disableInfiniteScroll() {
				return !this.logSearchResults.length || this.logSearchResultsExhausted;
			},
			displayInitialSearchSpinner() {
				return !this.logSearchResults.length && this.loadingLogs;
			},
			selectedSpecies() {
				return this.$store.state.search.searchParams.species;
			},
			sortingOptions() {
				if(this.isLogSearch) {
					return {
						0: 'label.logNo',
						1: 'label.palletNo',
						2: 'label.internalQuality',
						3: 'label.qualities'
					}
				}
				if(this.isPalletSearch) {
					return {
						1: 'label.palletNo',
            4: 'label.place'
					}
				}
			}
		},
		mounted() {
			if(!this.logSearchResults.length && !this.logSearchRequest) {
				this.loadResults();
			}
			this.$store.commit(SET_DETAIL_VIEW_BACK_LINK, {name: this.$route.name, params: this.$route.params});
		},
		methods: {
			async setSearchType(searchType) {
				if(searchType !== this.searchType) {
					this.switchingSearchTypes = true;

					this.$store.commit(SET_SEARCH_PARAMS, {searchType: searchType});
					this.$store.commit(SET_SEARCH_TYPE, searchType);
					await this.loadResults(1);

					this.switchingSearchTypes = false;
				}
			},
      setSearchDisplayType(searchDisplayType) {
          if(this.isPalletSearch) {
            if(searchDisplayType !== this.searchDisplayTypePallet) {
              this.$store.commit(SET_SEARCH_DISPLAY_TYPE_PALLET, searchDisplayType);
            }
          } else {
            if(searchDisplayType !== this.searchDisplayType) {
              this.$store.commit(SET_SEARCH_DISPLAY_TYPE, searchDisplayType);
            }
          }
      },
			setSorting(orderBy, disabled) {
				this.$store.commit(SET_SEARCH_PARAMS, {orderBy: [orderBy]});
				if(!disabled) {
					this.loadResults();
				}
			},
			changeSortingDirection(disabled) {
				this.$store.commit(SET_SEARCH_PARAMS, {orderDirection: [!this.logSearchParams.orderDirection[0]]});
				if(!disabled) {
					this.loadResults();
				}
			}
		}
	}
</script>